import React from 'react';
import { Car, Truck, MapPin } from 'lucide-react';

/* ─── Design Tokens (match DemoSections / BookingPanels) ─── */
const PANEL_BG = '#171411';
const SURFACE = '#1e1b16';
const BORDER = '#2a2620';
const MONO = "'DM Mono', monospace";
/* Panel headings render at 18px — under the 1.375rem display floor — so they take
   the mono body face at 500 rather than the display serif. */
const DISPLAY = 'var(--s8-font-mono)';
const BODY = "'Barlow', sans-serif";

const AMBER = '#E6A23C';
const MUTED = '#a39889';
const TEXT = '#F0EBE0';

/* Parking type enum (backend ParkingType) → human label. */
const PARKING_TYPE_LABELS = {
  onsite: 'On-site',
  street: 'Street',
  garage: 'Garage',
  none: 'None',
  other: 'Other',
};

function Row({ icon: Icon, label, value, note, testId, isLast }) {
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-3 py-3.5 px-5"
      style={{ borderBottom: isLast ? 'none' : `1px solid ${BORDER}` }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ background: `${AMBER}14`, border: `1px solid ${AMBER}30` }}
      >
        <Icon size={15} color={AMBER} />
      </div>
      <div className="min-w-0">
        <p style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: 0.4, textTransform: 'uppercase' }}>
          {label}
        </p>
        {value && (
          <p className="mt-0.5" style={{ fontFamily: BODY, fontSize: 14, fontWeight: 600, color: TEXT }}>
            {value}
          </p>
        )}
        {note && (
          <p className="mt-1" style={{ fontFamily: BODY, fontSize: 13, color: '#c4baac', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
            {note}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * ParkingArrival — display-only Showtime panel.
 * Reads the day-of parking bundle propagated onto the booking by Element 1
 * (booking.dayOf.parking) plus the locked venue address (snapshot.venueAddress).
 * Renders nothing when no parking / loading / address data is present, so it
 * degrades gracefully until the parked parking branch lands on main.
 */
export function ParkingArrival({ parking, venueAddress }) {
  const p = parking || {};
  const typeLabel = p.parkingType ? (PARKING_TYPE_LABELS[p.parkingType] || p.parkingType) : null;
  const parkingNote = p.parkingNote && p.parkingNote.trim() ? p.parkingNote.trim() : null;
  const loadingNote = p.loadingZoneNote && p.loadingZoneNote.trim() ? p.loadingZoneNote.trim() : null;
  const address = venueAddress && venueAddress.trim() ? venueAddress.trim() : null;

  const hasParking = !!(typeLabel || parkingNote);
  const hasLoading = !!p.loadingZone;
  const hasAddress = !!address;

  // Graceful empty — nothing to show until the data exists.
  if (!hasParking && !hasLoading && !hasAddress) return null;

  // Build rows then mark the last one (so we don't draw a trailing divider).
  const rows = [];
  if (hasAddress) rows.push({ key: 'address', icon: MapPin, label: 'Venue address', value: address });
  if (hasParking) rows.push({ key: 'parking', icon: Car, label: 'Parking', value: typeLabel, note: parkingNote });
  if (hasLoading) rows.push({ key: 'loading', icon: Truck, label: 'Loading zone', value: 'Available', note: loadingNote });

  return (
    <div
      data-testid="parking-arrival"
      className="mb-6 overflow-hidden"
      style={{
        background: PANEL_BG,
        borderRadius: 16,
        border: '1px solid rgba(212, 169, 90, 0.12)',
      }}
    >
      <div className="px-5 py-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <h2 style={{ fontFamily: DISPLAY, fontWeight: 500, fontSize: 18, letterSpacing: '0.04em', color: TEXT }}>
          PARKING &amp; ARRIVAL
        </h2>
      </div>
      <div style={{ background: SURFACE }}>
        {rows.map((r, i) => (
          <Row
            key={r.key}
            icon={r.icon}
            label={r.label}
            value={r.value}
            note={r.note}
            testId={`parking-row-${r.key}`}
            isLast={i === rows.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export default ParkingArrival;
